import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { pickLine, shortPickLine } from '../../../../../features/fulfillment/fulfillmentThunks';
import {
  DISCREPANCY_REASON_OPTIONS,
  defaultLotOf,
  lotRequired,
  pendingOf,
  pickDoneNotice,
  pickProblem,
  reservedOf,
  shortPickDoneNotice,
  shortPickProblem,
} from '../../../../../features/fulfillment/fulfillmentUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

/**
 * Takes units of one line out of a location.
 *
 * The suggestions come from the server in route order — the first is the
 * nearest — with their lots in FEFO order, and the dialog opens on them. The
 * scanned code is optional: when it is typed the server checks it belongs to
 * this product before moving anything, and Enter confirms as a scanner would.
 *
 * The second half is for when the shelf does not hold what the system says.
 * Reporting that **does not adjust the inventory**: "I did not find it" and
 * "it does not exist" are not the same thing, and confusing them is how a
 * kardex gets corrupted. It picks what was found, if anything, and leaves a
 * counting task.
 *
 * @param {object} props
 * @param {object|null} props.line - The picklist line; `null` keeps it closed.
 * @param {number} props.orderId
 * @param {() => void} props.onClose
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const PickLineModal = ({ line, orderId, onClose, onDone }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector((state) => state.fulfillment.busyLineId === line?.order_product_id);

  const [slotId, setSlotId] = useState(null);
  const [lot, setLot] = useState('');
  const [quantity, setQuantity] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [reporting, setReporting] = useState(false);
  const [found, setFound] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [saveError, setSaveError] = useState(null);

  const lineId = line?.order_product_id;

  // Every opening starts on the nearest location and on everything pending.
  useEffect(() => {
    if (!line) return;
    const nearest = line.suggestions?.[0] || null;
    setSlotId(nearest?.slot_id ?? null);
    setLot(defaultLotOf(nearest));
    // Everything pending, but never more than the reservation promises.
    setQuantity(String(Math.min(pendingOf(line), reservedOf(line) || pendingOf(line))));
    setScannedCode('');
    setReporting(false);
    setFound('');
    setReason('');
    setNotes('');
    setSaveError(null);
    // The line object is replaced by every reload behind this dialog;
    // reseeding on that would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  if (!line) return null;

  const suggestions = line.suggestions || [];
  const suggestion = suggestions.find((item) => item.slot_id === slotId) || null;
  const max = pendingOf(line);
  const reserved = reservedOf(line);
  const lots = suggestion?.lots || [];

  const problem = pickProblem({ suggestion, quantity, max, lot, reserved });
  const reportProblem = shortPickProblem({ suggestion, found, reason });

  const slotOptions = suggestions.map((item, index) => ({
    value: item.slot_id,
    label: `${item.slot_name} — ${formatQuantity(item.available)} disponibles${
      index === 0 ? ' · más cerca' : ''
    }`,
    shortLabel: item.slot_name,
  }));
  const lotOptions = lots.map((item) => ({
    value: item.lot,
    label: `${item.lot} · ${formatQuantity(item.quantity)}`,
  }));

  const finish = (action, notice) => {
    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }
    onDone(notice(action.payload));
    onClose();
  };

  const handlePick = async () => {
    if (problem || isSaving) return;
    setSaveError(null);
    const action = await dispatch(
      pickLine({ orderId, line, suggestion, quantity, lot, scannedCode })
    );
    finish(action, (data) =>
      pickDoneNotice({ line, quantity, suggestion, capacityWarning: data?.capacity_warning })
    );
  };

  const handleReport = async () => {
    if (reportProblem || isSaving) return;
    setSaveError(null);
    const action = await dispatch(
      shortPickLine({ orderId, line, suggestion, found, reason, lot, notes })
    );
    finish(action, () => shortPickDoneNotice({ line, found, suggestion }));
  };

  return (
    <Modal isOpen onClose={onClose} title={`Pickear ${line.sku}`} testId="pick-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {line.product_name} · faltan {formatQuantity(max)} {line.base_uom || 'un'}
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="pick-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        {suggestions.length === 0 ? (
          <div
            data-testid="pick-no-stock"
            className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-xl p-3 text-sm text-ninja-dark dark:text-white"
          >
            No hay stock disponible de este producto en ninguna ubicación pickeable de esta
            bodega. Si la mercadería está en recepción, falta guardarla en Almacenado.
          </div>
        ) : (
          <>
            <FilterableSelect
              variant="form"
              testId="pick-slot"
              label="Ubicación"
              value={slotId}
              onChange={(value) => {
                setSlotId(value);
                setLot(defaultLotOf(suggestions.find((item) => item.slot_id === value)));
              }}
              options={slotOptions}
              placeholder="Seleccionar..."
              searchPlaceholder="Buscar ubicación..."
              disabled={isSaving}
            />
            <p className="text-xs text-text-muted -mt-2">
              Ordenadas por recorrido: la primera es la más cercana al punto de preparación.
            </p>

            {lotRequired(suggestion) && (
              <FilterableSelect
                variant="form"
                testId="pick-lot"
                label="Lote"
                value={lot || null}
                onChange={setLot}
                options={lotOptions}
                placeholder="Elegir lote..."
                searchPlaceholder="Buscar lote..."
                disabled={isSaving}
              />
            )}
            {lotRequired(suggestion) && (
              <p className="text-xs text-text-muted -mt-2">
                Ordenados por vencimiento primero, y antigüedad después.
              </p>
            )}

            {!reporting && (
              <>
                <Field label={`Cantidad (máximo ${formatQuantity(max)})`} htmlFor="pick-quantity">
                  <input
                    id="pick-quantity"
                    type="number"
                    min="0"
                    step="1"
                    data-testid="pick-quantity"
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  />
                </Field>

                <Field
                  label="Código escaneado"
                  htmlFor="pick-code"
                  hint="Opcional. Si lo escribes, el servidor comprueba que corresponde a este producto antes de mover nada."
                >
                  <input
                    id="pick-code"
                    type="text"
                    data-testid="pick-code"
                    value={scannedCode}
                    onChange={(event) => setScannedCode(event.target.value)}
                    // A scanner ends its read with Enter: that is the confirmation.
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') handlePick();
                    }}
                    placeholder="Pega o escanea el código del producto"
                    autoComplete="off"
                    disabled={isSaving}
                    className={inputClass}
                  />
                </Field>
              </>
            )}

            <div className="border-t border-border pt-4">
              {!reporting ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReporting(true)}
                  disabled={isSaving}
                  data-testid="pick-report-toggle"
                >
                  No está lo que dice el sistema
                </Button>
              ) : (
                <div className="space-y-4" data-testid="pick-report">
                  <div className="bg-ninja-greenLight/60 dark:bg-ninja-green/10 rounded-xl p-3 text-xs text-text-secondary">
                    Reportar un desvío <strong>no ajusta el inventario</strong>: pickea lo que sí
                    había y deja una tarea de conteo. "No lo encontré" y "no existe" no son lo mismo.
                  </div>

                  <Field label="Cuánto había de verdad" htmlFor="pick-found">
                    <input
                      id="pick-found"
                      type="number"
                      min="0"
                      step="1"
                      data-testid="pick-found"
                      value={found}
                      onChange={(event) => setFound(event.target.value)}
                      disabled={isSaving}
                      className={inputClass}
                    />
                  </Field>

                  <FilterableSelect
                    variant="form"
                    testId="pick-reason"
                    label="Motivo"
                    value={reason || null}
                    onChange={setReason}
                    options={DISCREPANCY_REASON_OPTIONS}
                    placeholder="Elegir motivo..."
                    searchPlaceholder="Buscar..."
                    disabled={isSaving}
                  />

                  <Field label="Nota" htmlFor="pick-notes" hint="Opcional.">
                    <input
                      id="pick-notes"
                      type="text"
                      data-testid="pick-notes"
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      disabled={isSaving}
                      className={inputClass}
                    />
                  </Field>

                  <div className="flex justify-between gap-3">
                    <Button variant="ghost" size="sm" onClick={() => setReporting(false)} disabled={isSaving}>
                      Volver a pickear
                    </Button>
                    <Button
                      variant="secondary"
                      loading={isSaving}
                      loadingLabel="Registrando..."
                      disabled={Boolean(reportProblem)}
                      onClick={handleReport}
                      data-testid="pick-report-save"
                    >
                      {reportProblem || 'Reportar desvío'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        {!reporting && suggestions.length > 0 && (
          // Disabled, it says what is missing.
          <Button
            variant="primary"
            loading={isSaving}
            loadingLabel="Pickeando..."
            disabled={Boolean(problem)}
            onClick={handlePick}
            data-testid="pick-save"
          >
            {problem || 'Pickear'}
          </Button>
        )}
      </div>
    </Modal>
  );
};
